import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { COOKIE_CONSENT_KEY } from "@/legal/legalConfig";

/**
 * Sprint 7 — Cookie-Banner für revenioo.ai.
 *
 * Verhalten:
 * - Zeigt sich einmalig, wenn localStorage.revenioo_consent nicht gesetzt
 * - "OK, verstanden" speichert: { essential:true, analytics:false, marketing:false, ts:isoDate }
 * - "Details" verlinkt zu /cookies (zeigt Banner weiter, bis OK)
 * - Wird auf /cookies selbst NICHT eingeblendet (vermeidet Banner über eigener Cookie-Seite)
 *
 * Kein Kategorie-Toggle in Sprint 7 (Etikettenschwindel, solange keine optionalen Cookies existieren).
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    try {
      const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!raw) {
        Promise.resolve().then(() => { if (alive) setVisible(true); });
      }
    } catch {
      // SSR/privacy mode → leave invisible (initial state)
    }
    return () => { alive = false; };
  }, []);

  // Banner nicht auf der Cookie-Seite selbst
  if (location.pathname === "/cookies") return null;
  // Sprint 9 — kein Cookie-Banner im Embed-Konfigurator (eigener Consent im Form)
  if (location.pathname.startsWith("/embed/")) return null;
  if (!visible) return null;

  const accept = () => {
    try {
      const payload = {
        essential: true,
        analytics: false,
        marketing: false,
        ts: new Date().toISOString(),
      };
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
      className="fixed bottom-0 inset-x-0 z-[80] bg-white border-t border-slate-200 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]"
    >
      <div className="max-w-5xl mx-auto px-5 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-slate-700 leading-relaxed">
          revenioo.ai verwendet ausschließlich notwendige Cookies für Login, Sicherheit und den Betrieb der Plattform.
          Analyse- und Marketing-Cookies werden aktuell nicht eingesetzt.
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/cookies"
            data-testid="cookie-banner-details"
            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
          >
            Details
          </Link>
          <button
            type="button"
            onClick={accept}
            data-testid="cookie-banner-accept"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-md"
          >
            OK, verstanden
          </button>
        </div>
      </div>
    </div>
  );
}
